import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../services/api";
import { toast } from "react-toastify";
import { catchError } from "../utils/catchError";
import { AxiosError } from "axios";
import { CommonHeaderProperties } from "@/interfaces/user";
import { IDecodedJwt } from "@/interfaces/jwtDecoded";
import jwtDecode from "jwt-decode";

type SignInCredentials = {
  email: string;
  password: string;
};

type AuthContextData = {
  signIn: (
    credentials: SignInCredentials
  ) => Promise<{ error: { status: number; userId: number } } | void>;
  signOut: () => void;
  changeToken: (token: string) => void;
  decodedToken: IDecodedJwt;
  isAuthenticated: boolean;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthContext = createContext({} as AuthContextData);

export async function updateUserType(type: string) {
  await api.put("/users/update", { type }).catch(() => {
    toast.error("Algo deu errado, tente novamente");
    // return <Navigate to="/" replace />
  });
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [isAuthenticated, setIsAuthenticated] = useState(
    !!localStorage.getItem("futToken")
  );
  const [decodedToken, setDecodedToken] = useState<IDecodedJwt>(
    localStorage.getItem("futToken")
      ? jwtDecode(localStorage.getItem("futToken")!)
      : {
        id: 0,
        email: "",
        exp: 0,
        iat: 0,
        chargeStatus: "pending",
        isActive: false,
      }
  );

  const navigate = useNavigate();
  const authChannel = new BroadcastChannel("auth");
  const messageReceiver = new BroadcastChannel("auth");

  useEffect(() => {
    messageReceiver.onmessage = (message) => {
      switch (message.data) {
        case "signOut":
          localStorage.removeItem("futToken");
          setIsAuthenticated(false);
          navigate("/login");
          break;
        default:
          break;
      }
    };
  }, []);

  function signOut() {
    authChannel.postMessage("signOut");
  }

  function changeToken(token: string) {
    if (!token || token === "undefined") {
      return;
    }
    try {
      localStorage.setItem("futToken", token);
      setIsAuthenticated(!!token);
      setDecodedToken(jwtDecode(token));
      api.defaults.headers = {
        Authorization: `Bearer ${token}`,
      } as CommonHeaderProperties;
    } catch (e) { }
  }

  async function signIn({ email, password }: SignInCredentials) {
    try {
      const response = await api.post("user/login ", {
        email,
        password,
      });
      const body = response.data;
      console.log(response.data,"response");
      console.log(body,"body");
      changeToken(body.token);
      toast.success("Login feito com sucesso!");
      navigate("/dashboard");
      return;
    } catch (error) {
      const typeError = error as AxiosError<any>;

      if (typeError.response?.status === 402) {
        return {
          error: { status: 402, userId: typeError.response.data.userId },
        };
      }

      const message = catchError(error);
      toast.error(message);
      return;
    }
  }

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        signIn,
        signOut,
        decodedToken,
        changeToken,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }

  return context;
}
